/**
 * Sync-Konfiguration – Server, Zugangsschlüssel und Bereichsgeheimnis dieses
 * Geräts sowie der Kopplungscode, mit dem weitere Geräte dazukommen.
 *
 * Der Kopplungscode enthält alles, was ein Gerät braucht: Wer ihn hat, hat
 * vollen Zugriff auf den Bereich, auch auf den API-Schlüssel.
 */

import { toBase64Url, fromBase64Url } from './syncCrypto.js';

const STORAGE_KEY = 'the-reader-sync';
const CODE_PREFIX = 'trs1.';

/**
 * @typedef {Object} SyncConfig
 * @property {string} serverUrl - ohne abschließenden Schrägstrich
 * @property {string} token - Zugangsschlüssel des Servers
 * @property {string} secret - Bereichsgeheimnis, base64url
 */

/**
 * @returns {SyncConfig|null}
 */
export function loadConfig() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
}

/**
 * @param {SyncConfig} config
 */
export function saveConfig(config) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
}

export function clearConfig() {
  localStorage.removeItem(STORAGE_KEY);
}

/**
 * Prüft und vereinheitlicht eine Server-Adresse. Erlaubt ist https, für die
 * Entwicklung auch http auf diesem Rechner.
 * @param {string} input
 * @returns {string}
 */
export function normalizeServerUrl(input) {
  let url;
  try {
    url = new URL(input.trim());
  } catch {
    throw new Error('Die Server-Adresse ist keine gültige URL.');
  }
  const isLocal = ['localhost', '127.0.0.1', '[::1]'].includes(url.hostname);
  if (url.protocol !== 'https:' && !(url.protocol === 'http:' && isLocal)) {
    throw new Error('Die Server-Adresse muss mit https:// beginnen.');
  }
  return `${url.origin}${url.pathname}`.replace(/\/+$/, '');
}

/**
 * @param {SyncConfig} config
 * @returns {string}
 */
export function encodePairingCode(config) {
  const payload = JSON.stringify({ s: config.serverUrl, t: config.token, k: config.secret });
  return CODE_PREFIX + toBase64Url(new TextEncoder().encode(payload));
}

/**
 * @param {string} code
 * @returns {SyncConfig}
 */
export function decodePairingCode(code) {
  const trimmed = code.trim();
  try {
    if (!trimmed.startsWith(CODE_PREFIX)) throw new Error('prefix');
    const payload = JSON.parse(
      new TextDecoder().decode(fromBase64Url(trimmed.slice(CODE_PREFIX.length)))
    );
    if (!payload.s || !payload.t || fromBase64Url(payload.k).length !== 32) {
      throw new Error('fields');
    }
    return { serverUrl: normalizeServerUrl(payload.s), token: payload.t, secret: payload.k };
  } catch {
    throw new Error('Das ist kein gültiger Kopplungscode.');
  }
}

/**
 * Link, der den Reader öffnet und den Code zum Koppeln vorschlägt. Der Code
 * steht im Fragment – das geht nie an einen Server.
 * @param {string} code
 * @returns {string}
 */
export function pairingLink(code) {
  return `${location.origin}${location.pathname}#koppeln=${code}`;
}

/**
 * Liest einen Kopplungscode aus der Adresse, falls die App über einen
 * Kopplungslink geöffnet wurde
 * @returns {string|null}
 */
export function pairingCodeFromLocation() {
  const match = location.hash.match(/^#koppeln=(.+)$/);
  if (!match) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return null; // kaputt kodiert – wie kein Code; decodePairingCode meldet sonst den Fehler
  }
}
